import { config } from "../config";
import { redact } from "./redact";
import type {
  ErrorReporter,
  LogContext,
  LogEntry,
  LogInput,
  Logger,
} from "./types";

/**
 * Console is enabled ONLY in dev. Gated on `@core/config`'s `isDev` (the single source of
 * truth for env) — in a production build this is `false`, so every `if (CONSOLE_ENABLED)`
 * guard short-circuits and the console stays dead silent. Non-negotiable: prod never logs to
 * the console; server-side failures reach the prod error sink via `log.error` instead.
 */
export const CONSOLE_ENABLED = config.isDev;

// --- prod error sink seam ---------------------------------------------------------------
// log.error is the SINGLE error entry point. In dev it writes to the console; in production
// it writes NOTHING to the console and instead forwards the redacted entry here.
//
// TODO(prod-error-sink): register a real reporter (Sentry / GlitchTip / …) at app bootstrap
// once the residency/deployment decision is made. Destination deferred on purpose — service
// code calls log.error, never a monitoring SDK directly.
let reportError: ErrorReporter = () => {
  // no-op until registered
};

export function registerErrorReporter(reporter: ErrorReporter): void {
  reportError = reporter;
}

function normalizeInput(input: LogInput | undefined): LogContext {
  if (input === undefined) return {};
  if (typeof input === "string") return { context: input };
  return input;
}

function redactMeta(input: LogInput | undefined): Record<string, unknown> {
  return redact(normalizeInput(input)) as Record<string, unknown>;
}

/**
 * Create a namespaced service-layer logger. Namespaces are `module:feature` (kebab:colon).
 * Use in services, not components/hooks.
 */
export function createLogger(namespace: string): Logger {
  const prefix = `[${namespace}]`;

  const emit = (
    level: "log" | "info" | "warn",
    message: string,
    input?: LogInput
  ): void => {
    if (!CONSOLE_ENABLED) return; // prod: dead code, zero console.* calls
    console[level](prefix, message, redactMeta(input));
  };

  return {
    log: (message, input) => emit("log", message, input),
    info: (message, input) => emit("info", message, input),
    warn: (message, input) => emit("warn", message, input),
    error: (message, input) => {
      // Redact BEFORE the console or the sink sees anything.
      const entry: LogEntry = {
        level: "error",
        namespace,
        message,
        meta: redactMeta(input),
      };
      if (CONSOLE_ENABLED) {
        console.error(prefix, message, entry.meta);
      }
      // Always forwarded; the default is a no-op, and it's the ONLY prod destination.
      reportError(entry);
    },
  };
}
